import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import IntroSection from "../components/IntroSection";
import IntroSectionSkeleton from "../components/IntroSectionSkeleton";
import PageHero from "../components/PageHero";
import { api } from "../lib/api";
import { useIntro } from "../hooks/useIntro";
import { toast } from "sonner";
import { Calculator, TrendingUp, Percent, PieChart, ArrowRight, ArrowLeft, Save, Eye, X, FolderPlus, Loader2, Lock } from "lucide-react";
import SidebarToggleButton from "../components/SidebarToggleButton";
import { useMenuVisibility } from "../context/MenuVisibilityContext";
import CapTableTool from "../components/tools/CapTableTool";
import ValueChainTool from "../components/tools/ValueChainTool";
import RetailMarginTool from "../components/tools/RetailMarginTool";
import SalesEstimatorTool from "../components/tools/SalesEstimatorTool";

export const TOOLS = [
  {
    slug: "value-chain",
    name: "Value Chain Calculator",
    icon: Percent,
    description: "Model your unit economics from cost of goods through distributor, retailer and MSRP — with margin, promo and slotting layered in.",
    // Native React component (no iframe) since Oct 2026 — see
    // src/components/tools/ValueChainTool.jsx. Saved snapshots keep the
    // same `value-chain-1` format.
    native: ValueChainTool,
  },
  {
    slug: "retail-margin",
    name: "Retail Margin Calculator",
    icon: Calculator,
    description: "Model retailer margin, list pricing, promo lifts and shelf economics across channels.",
    // Native (no iframe) since Oct 2026 — the original engine runs in the
    // app page; see src/components/tools/RetailMarginTool.jsx.
    native: RetailMarginTool,
  },
  {
    slug: "sales-estimator",
    name: "Financial Projection Tool",
    icon: TrendingUp,
    description: "Project your revenue, retailer volume and shelf turns to model a realistic financial trajectory.",
    // Native (no iframe) since Oct 2026 — the original engine runs in the
    // app page; see src/components/tools/SalesEstimatorTool.jsx.
    native: SalesEstimatorTool,
  },
  {
    slug: "cap-table",
    name: "Cap Table Modeler",
    icon: PieChart,
    description: "Track founder equity, employee options, SAFEs and priced rounds. See fully-diluted ownership shift with every scenario — with plain-English explanations of every term.",
    // Native React component (no iframe) since Oct 2026 — see
    // src/components/tools/CapTableTool.jsx. Saved snapshots keep the
    // same `cap-table-1` format and localStorage key.
    native: CapTableTool,
  },
];

// Paste ANY of these into videoUrl and IntroSection auto-picks the renderer:
//   • YouTube share URL         "https://youtu.be/XXXXXXX"
//   • YouTube embed URL         "https://www.youtube.com/embed/XXXXXXX"
//   • Vimeo player URL          "https://player.vimeo.com/video/XXXXXXX"
//   • Direct MP4 / WEBM / MOV   "https://cdn.example.com/intro.mp4"
export const TOOLS_INTRO = {
  eyebrow: "Financial Tools Intro",
  title: "Model the money before you make the money.",
  body: "Four purpose-built calculators sit under this section — value chain, retail margin, a full 2/3/5-year financial projection, and a cap-table modeler that shows dilution across every round. Watch the short walkthrough, then jump into whichever tool matches the question you're trying to answer today.",
  videoUrl: "",
  bullets: [
    "Model unit economics from COGS to MSRP",
    "Pressure-test retailer margins and slotting fees",
    "Project revenue, pallets, and shelf turns over 2, 3, or 5 years",
    "Track founder equity, options, SAFEs and priced rounds through every dilution event",
  ],
};

export function ToolsIndex() {
  const { intro, ready: introReady } = useIntro("tools", TOOLS_INTRO);
  // Plan-based access for each Financial Tool comes from the Menu
  // Directory (Admin Console → Menu Directory → nav-tools → per-child
  // `plans` list). We NEVER duplicate that logic here — the context
  // exposes `isChildPlanGated("nav-tools", <slug>)` backed by the same
  // `/api/menu-visibility` payload the sidebar uses, so this page and
  // the sidebar always agree in every environment (preview + live).
  const { isChildPlanGated } = useMenuVisibility();
  return (
    <AppShell title="Financial Tools Intro">
      <div className="w-full">
        {introReady && intro ? (
          <IntroSection
            {...intro}
            ctaLabel="Jump to the tools"
            ctaHref="#tools-grid"
            allowSeek
            configHint={<>Admin → Intros to edit copy + video. Fallback in <code>src/pages/Tools.jsx</code>.</>}
            testId="tools-intro"
          />
        ) : (
          <IntroSectionSkeleton testId="tools-intro" />
        )}

        <div id="tools-grid" className="pt-2 mt-10">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="font-display text-3xl">The tools</h2>
            <p className="text-[12px] text-muted-foreground">{TOOLS.length} calculators · updated 2026</p>
          </div>

          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {TOOLS.map((t) => {
              const planGated = isChildPlanGated("nav-tools", t.slug);
              return (
                <div key={t.slug} className="surface p-6 flex flex-col" data-testid={`tool-card-${t.slug}`}>
                  <div className="w-11 h-11 rounded-xl bg-shell-accent/25 grid place-items-center">
                    <t.icon className="w-5 h-5 text-shell-side" />
                  </div>
                  <h3 className="font-display text-2xl mt-4">{t.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground flex-1">{t.description}</p>
                  {!t.native ? (
                    <span className="mt-5 text-xs text-muted-foreground uppercase tracking-widest">Coming soon</span>
                  ) : planGated ? (
                    // Menu Directory says this tool's `plans` list
                    // excludes the current user's plan. Route them to
                    // /account/subscription where the Founder / Founder
                    // Pro one-time checkouts live. Same destination the
                    // sidebar plan-gate links use for consistency.
                    <Link
                      to="/account/subscription"
                      className="btn-secondary mt-5 self-start inline-flex items-center gap-1.5"
                      data-testid={`tool-upgrade-${t.slug}`}
                    >
                      <Lock className="w-3.5 h-3.5" /> Upgrade to unlock
                    </Link>
                  ) : (
                    <Link to={`/tools/${t.slug}`} className="btn-primary mt-5 self-start" data-testid={`tool-open-${t.slug}`}>
                      Open tool <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

export function ToolEmbed() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const snapshotId = search.get("snapshot");
  const tool = TOOLS.find((t) => t.slug === slug);
  // Every tool is a native component (no iframe) exposing the cpg* bridge
  // (cpgCollectState / cpgApplyState / cpgSetViewOnly) through this ref.
  const nativeRef = React.useRef(null);
  const NativeTool = tool?.native;
  const getBridge = () => nativeRef.current;
  // ── Financial Projection save/load bridge state ─────────────────────
  // All four tools support Save-to-Projects through the same bridge.
  const isFinancial = ["sales-estimator", "value-chain", "retail-margin", "cap-table"].includes(slug);
  const [snapshot, setSnapshot] = useState(null); // loaded project doc when ?snapshot=<id>
  const [readOnly, setReadOnly] = useState(false);
  const [saveMode, setSaveMode] = useState(null); // 'draft' | 'result' | null (modal closed)
  const [bridgeReady, setBridgeReady] = useState(false);
  const hydratedRef = useRef(false);

  // ── Financial: mark the tool ready once its bridge fns are installed. ──
  // The bridge lives at the bottom of sales-estimator.html so it's
  // available shortly after `load`. Poll briefly instead of racing.
  useEffect(() => {
    if (!isFinancial) return;
    let cancelled = false;
    const check = () => {
      const w = getBridge();
      if (w && typeof w.cpgCollectState === "function") {
        if (!cancelled) setBridgeReady(true);
        return;
      }
      if (!cancelled) setTimeout(check, 120);
    };
    setBridgeReady(false);
    hydratedRef.current = false;
    check();
  }, [isFinancial, slug, snapshotId]);

  // ── Financial: fetch the snapshot doc when ?snapshot=<id> present. ──
  useEffect(() => {
    if (!isFinancial || !snapshotId) { setSnapshot(null); setReadOnly(false); return; }
    setReadOnly(false);
    let cancelled = false;
    api.get(`/projects/${snapshotId}`)
      .then((r) => { if (!cancelled) setSnapshot(r.data || null); })
      .catch(() => { if (!cancelled) toast.error("Couldn't load saved projection."); });
    return () => { cancelled = true; };
  }, [isFinancial, snapshotId]);

  // ── Financial: hydrate the tool once BOTH the bridge is ready AND the
  //     snapshot doc has loaded. Runs exactly once per snapshot load. ──
  useEffect(() => {
    if (!isFinancial || !bridgeReady || !snapshot) return;
    if (hydratedRef.current) return;
    const w = getBridge();
    const snap = snapshot.financial_snapshot;
    if (!w || !snap) return;
    try {
      w.cpgApplyState(snap);
      const ro = snapshot.financial_variant === "result";
      w.cpgSetViewOnly(ro);
      setReadOnly(ro);
      hydratedRef.current = true;
    } catch { /* ignore */ }
  }, [isFinancial, bridgeReady, snapshot]);

  const collectState = () => {
    const w = getBridge();
    if (!w || typeof w.cpgCollectState !== "function") return null;
    return w.cpgCollectState();
  };

  const handleSave = async ({ variant, parentId, projectName }) => {
    const snap = collectState();
    if (!snap) { toast.error("Tool isn't ready yet — try again in a moment."); return; }
    try {
      if (snapshotId && snapshot) {
        // Updating an existing draft in place. Never mutates the project
        // name unless the founder edited it in the modal.
        await api.patch(`/projects/${snapshotId}`, {
          name: projectName || snapshot.name,
          financial_snapshot: snap,
          financial_variant: variant,
          financial_saved_at: new Date().toISOString(),
        });
      } else {
        await api.post("/projects", {
          name: projectName,
          type: "financial_projection",
          parent_id: parentId || null,
          financial_snapshot: snap,
          financial_variant: variant,
          financial_kind: slug,
        });
      }
      toast.success(variant === "result"
        ? "Result saved to Projects."
        : "Draft saved to Projects.");
      setSaveMode(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    }
  };

  if (!tool || !tool.native) {
    return (
      <AppShell title="Financial Tools Intro">
        <div className="w-full">
          <p className="font-display text-3xl">Tool not available</p>
          <Link to="/tools" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-shell-side">
            <ArrowLeft className="w-4 h-4" /> Financial Tools Intro
          </Link>
        </div>
      </AppShell>
    );
  }
  return (
    <AppShell title={tool.name}>
      <div className="w-full">
        <Link to="/tools"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              data-testid="tool-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Financial Tools Intro
        </Link>
        <PageHero
          eyebrow="Financial Modeling"
          icon={tool.icon}
          title={<>{tool.name.split(" ").slice(0, -1).join(" ")} <span className="text-grad">{tool.name.split(" ").slice(-1)[0]}</span></>}
          subtitle={tool.description}
          right={<SidebarToggleButton testId="tool-sidebar-toggle" />}
          className="mt-2"
          testid={`tool-hero-${slug}`}
        />
        <div className="surface overflow-clip" style={{ padding: 0 }}>
          {isFinancial && readOnly && (
            <div
              className="flex flex-wrap items-center justify-end gap-2 px-4 py-3 border-b border-shell-border bg-muted/40"
              data-testid="financial-toolbar"
            >
              <span
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-pink-700 bg-pink-500/10 border border-pink-500/30 rounded-full px-2.5 py-1"
                data-testid="financial-viewonly-badge"
              >
                <Eye className="w-3 h-3" /> View-only snapshot
              </span>
            </div>
          )}
          <NativeTool
            ref={nativeRef}
            key={`${slug}-${snapshotId || "new"}`}
            onSave={(verb) => {
              if (readOnly) return;
              setSaveMode(verb === "save-result" ? "result" : "draft");
            }}
          />
        </div>
      </div>
      {saveMode && (
        <SaveFinancialModal
          mode={saveMode}
          toolName={tool.name}
          existingName={snapshot?.name}
          canPickFolder={!snapshotId}
          onCancel={() => setSaveMode(null)}
          onConfirm={(payload) => handleSave({ variant: saveMode, ...payload })}
        />
      )}
    </AppShell>
  );
}

// ── Save Financial Projection modal ──────────────────────────────────
// Mirrors the Launch Blueprint SaveAndSubmitModal so the founder gets
// the same folder-picker experience across both flows. Kept local to
// Tools.jsx (rather than extracted) so any future divergence in the
// two save flows doesn't fight a shared component.
function SaveFinancialModal({ mode = "draft", toolName = "Financial Projection", existingName, canPickFolder = true, onCancel, onConfirm }) {
  const isResult = mode === "result";
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoading] = useState(true);
  const [selectedFolder, setSelected] = useState("");
  const [name, setName] = useState(
    existingName || `${toolName} — ${new Date().toLocaleDateString()}`,
  );
  const [newFolder, setNewFolder] = useState("");
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canPickFolder) { setLoading(false); return; }
    api.get("/projects")
      .then((r) => setFolders((r.data || []).filter((p) => p.is_folder)))
      .catch(() => setFolders([]))
      .finally(() => setLoading(false));
  }, [canPickFolder]);

  const addFolder = async () => {
    const n = newFolder.trim();
    if (!n) return;
    setCreating(true);
    try {
      const r = await api.post("/projects", { name: n, is_folder: true });
      setFolders((prev) => [r.data, ...prev]);
      setSelected(r.data.id);
      setNewFolder("");
      toast.success(`Folder "${n}" created.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create folder.");
    } finally { setCreating(false); }
  };

  const confirm = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Give this projection a name so you can find it later."); return; }
    setSaving(true);
    try {
      await onConfirm({ parentId: selectedFolder || null, projectName: trimmed });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" role="dialog" aria-modal="true" data-testid="financial-save-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-shell-border flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-primary/10 text-primary">
            <Save className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-2xl">
              {isResult ? "Save Result to Projects" : "Save Draft to Projects"}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {isResult
                ? "Snapshot all steps + the final result into a Project. Saved results open in view-only mode so the numbers you shared with investors stay locked."
                : "Save your work so you can come back and continue editing from any step."}
            </p>
          </div>
          <button onClick={onCancel} className="p-1 rounded hover:bg-muted" aria-label="Close" data-testid="financial-save-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Save as</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="financial-save-name"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-shell-border outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          {canPickFolder && (
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Save into folder</label>
              {loadingFolders ? (
                <div className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Loading folders…</div>
              ) : (
                <select
                  value={selectedFolder}
                  onChange={(e) => setSelected(e.target.value)}
                  data-testid="financial-save-folder"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-shell-border bg-white outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">Root of Projects (no folder)</option>
                  {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              )}
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="text"
                  value={newFolder}
                  onChange={(e) => setNewFolder(e.target.value)}
                  placeholder="New folder name"
                  data-testid="financial-save-newfolder"
                  className="flex-1 px-3 py-2 rounded-lg border border-shell-border outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                />
                <button
                  type="button"
                  onClick={addFolder}
                  disabled={creating || !newFolder.trim()}
                  data-testid="financial-save-create-folder"
                  className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg bg-muted hover:bg-muted/70 disabled:opacity-40"
                >
                  <FolderPlus className="w-3.5 h-3.5" /> Create
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="p-4 bg-muted/40 border-t border-shell-border flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-semibold hover:bg-muted" data-testid="financial-save-cancel">Cancel</button>
          <button
            type="button"
            onClick={confirm}
            disabled={saving}
            data-testid="financial-save-confirm"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {isResult ? "Save Result" : "Save Draft"}
          </button>
        </div>
      </div>
    </div>
  );
}
